import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import TermsOfServiceModal from './TermsOfServiceModal';
import {
  fetchTermsOfServiceStatus,
  submitTermsOfServiceAgreement,
  TermsOfServiceContent,
} from './termsOfServiceApi';
import './TermsOfService.scss';

// 這些字不是條款內容，是前端自己的提示文字，後端不會提供。
const CHECK_FAILED_MESSAGE = '無法取得服務條款，請確認網路連線後再試一次。';
const RETRY_BUTTON_TEXT = '重試';
const SUBMIT_FAILED_MESSAGE = '送出失敗，請再試一次。';

type GateStatus =
  // 正在問後端需不需要同意；畫面已經被擋住。
  | 'checking'
  // 需要同意，條款彈窗顯示中。
  | 'agreementRequired'
  // 問不到答案，顯示錯誤與重試；使用者仍然被擋住。
  | 'checkFailed'
  // 不需要同意（或剛剛同意完成），遮罩收起，畫面交還給底下的內容。
  | 'passed';

// 由內往外找，看這個元素是不是位在一塊「允許自己捲動」的區域裡面。
// 有標記 body-scroll-lock-ignore 的容器（例如條款內文）即使在鎖定期間也能捲。
const isInsideScrollableArea = (element: HTMLElement): boolean => {
  let node: HTMLElement | null = element;

  while (node && node !== document.body) {
    if (node.hasAttribute && node.hasAttribute('body-scroll-lock-ignore')) {
      return true;
    }
    node = node.parentElement;
  }

  return false;
};

/**
 * 蓋在整個畫面最上層的遮罩。存在期間底層頁面點不到、也捲不動。
 * 掛載時鎖住底層捲動、卸載時解除，所以呼叫端只要決定「要不要渲染它」就好。
 */
const TermsOfServiceOverlay: FC<{ children: ReactNode }> = ({ children }) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const overlayElement = overlayRef.current;
    if (!overlayElement) {
      return undefined;
    }

    disableBodyScroll(overlayElement, { allowTouchMove: isInsideScrollableArea });

    return () => enableBodyScroll(overlayElement);
  }, []);

  return (
    <div className="terms-of-service-overlay" ref={overlayRef}>
      {children}
    </div>
  );
};

/**
 * 服務條款閘門。掛在網站根元件，使用者同意條款之前以遮罩把畫面擋住。
 *
 * 底下的內容照常掛載與初始化，只是被最上層的遮罩蓋住，點不到也捲不動。
 * 條款狀態只在網站第一次載入時問後端一次，之後不論換頁或重新渲染都不會再問。
 */
interface TermsOfServiceGateProps {
  enabled: boolean;
}

const TermsOfServiceGate: FC<TermsOfServiceGateProps> = ({ enabled }) => {
  const [status, setStatus] = useState<GateStatus>('checking');
  const [content, setContent] = useState<TermsOfServiceContent | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  // 確保進站只問一次；重新渲染或開發模式的重複掛載都不會再觸發。
  const hasCheckedRef = useRef(false);

  const checkTermsOfService = useCallback(async () => {
    setStatus('checking');

    try {
      const termsOfServiceStatus = await fetchTermsOfServiceStatus();

      if (!termsOfServiceStatus.needsAgreement || !termsOfServiceStatus.content) {
        setStatus('passed');
        return;
      }

      setContent(termsOfServiceStatus.content);
      setStatus('agreementRequired');
    } catch (error) {
      console.error('取得服務條款失敗', error);
      setStatus('checkFailed');
    }
  }, []);

  useEffect(() => {
    if (!enabled || hasCheckedRef.current) {
      return;
    }
    hasCheckedRef.current = true;

    void checkTermsOfService();
  }, [checkTermsOfService, enabled]);

  const handleAgree = useCallback(async () => {
    if (!content || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitErrorMessage(null);

    try {
      await submitTermsOfServiceAgreement(content.tosId);
      setStatus('passed');
    } catch (error) {
      console.error('送出服務條款同意失敗', error);
      setSubmitErrorMessage(SUBMIT_FAILED_MESSAGE);
      setIsSubmitting(false);
    }
  }, [content, isSubmitting]);

  if (!enabled || status === 'passed') {
    return null;
  }

  // 遮罩掛到 document.body，確保它疊在頁面上所有內容之上，不受任何父層樣式影響。
  const overlay = (
    <TermsOfServiceOverlay>
      {status === 'checking' && <div className="terms-of-service-spinner" />}

      {status === 'checkFailed' && (
        <div className="terms-of-service-notice">
          <p className="terms-of-service-notice__message">{CHECK_FAILED_MESSAGE}</p>
          <button
            type="button"
            className="terms-of-service-notice__retry-button"
            onClick={() => void checkTermsOfService()}
          >
            {RETRY_BUTTON_TEXT}
          </button>
        </div>
      )}

      {status === 'agreementRequired' && content && (
        <TermsOfServiceModal
          title="服務條款"
          contents={content.contents}
          agreeButtonText="同意"
          isSubmitting={isSubmitting}
          errorMessage={submitErrorMessage}
          onAgree={() => void handleAgree()}
        />
      )}
    </TermsOfServiceOverlay>
  );

  return createPortal(overlay, document.body);
};

export default TermsOfServiceGate;
